// MARK://dhk8/pos/pages/ReportsPage
// Reportes de turno/día — ventas, productos top, mesero top, propinas (reparto de pool)
// y la última varianza de corte Z. Todo se agrega en el cliente a partir de
// dhk8-pos-orders.listOrders (status=CLOSED, from/to), dhk8-pos-tips.summary y el
// último corte Z de dhk8-pos-cashcuts. (No hay servicio de reportes — por diseño.)

import { useEffect, useMemo, useState, type ReactNode } from 'react'

import { listOrders, type BackendOrderDto } from '../lib/clients/orders.js'
import { tipSummary, type TipSummaryDto } from '../lib/clients/tips.js'
import { latestCashCut, type CashCutDto } from '../lib/clients/cashcuts.js'
import { gqlClient } from '../lib/gql.js'
import { PosReportTaxesQuery, PosReportPaymentMethodsQuery, PosReportVoidsDiscountsQuery, PosReportSalesTotalsQuery } from '../lib/queries/reports.queries.js'
import { useAuthStore } from '../stores/auth-store.js'
import { useTenantId } from '../hooks/useTenantId.js'
import { formatCents } from '../lib/money.js'
// Lane #171 — charts NintaiGO (wrapper recharts + tokens existentes, sin Tailwind).
import { AreaChartNG, BarChartNG, PieChartNG } from '@dockethub/ui/charts'

// Zona de negocio: SIEMPRE GDL (America/Mexico_City = UTC-6, sin horario de
// verano). El "día" de los reportes es el día-calendario GDL, sin importar el
// huso horario del navegador/terminal — así cuadra con el admin (que agrega por
// día GDL on-read).
const GDL_OFFSET = '-06:00'
const GDL_MS = 6 * 3_600_000
/** Fecha-calendario GDL (yyyy-mm-dd) de un instante ISO. */
function gdlYmd(iso: string): string { return new Date(new Date(iso).getTime() - GDL_MS).toISOString().slice(0, 10) }
/** Hora del día (0-23) en GDL de un instante ISO. */
function gdlHour(iso: string): number { return new Date(new Date(iso).getTime() - GDL_MS).getUTCHours() }
/** Hoy (yyyy-mm-dd) en GDL. */
function gdlToday(): string { return new Date(Date.now() - GDL_MS).toISOString().slice(0, 10) }
function toDateInput(iso: string): string { return iso.slice(0, 10) }
/** Instante UTC del inicio (00:00) o fin (23:59:59.999) del día de negocio GDL `s`. */
function fromDateInput(s: string, end: boolean): string { return new Date(`${s}T${end ? '23:59:59.999' : '00:00:00.000'}${GDL_OFFSET}`).toISOString() }
function shortId(id: string | null | undefined): string { return id ? id.slice(0, 8) : '—' }

// Ola 2 — paridad con el admin (IVA, métodos de pago, cancelaciones/descuentos).
const METHOD_LABELS: Record<string, string> = { CASH: 'Efectivo', CARD: 'Tarjeta', TRANSFER: 'Transferencia', VOUCHER: 'Vale', OTHER: 'Otro' }

/** Rango (yyyy-mm-dd) inmediatamente anterior, misma duración, para el comparativo. */
function prevRange(fromDay: string, toDay: string): { from: string; to: string } {
  const f = new Date(`${fromDay}T00:00:00Z`).getTime()
  const t = new Date(`${toDay}T00:00:00Z`).getTime()
  const days = Math.max(1, Math.round((t - f) / 86_400_000) + 1)
  return {
    from: new Date(f - days * 86_400_000).toISOString().slice(0, 10),
    to: new Date(f - 86_400_000).toISOString().slice(0, 10),
  }
}

/** yyyy-mm-dd n días antes de `ymd` (aritmética de calendario, TZ-agnóstica). */
function ymdDaysAgo(ymd: string, n: number): string {
  const d = new Date(`${ymd}T00:00:00Z`); d.setUTCDate(d.getUTCDate() - n); return d.toISOString().slice(0, 10)
}

/** Presets de temporalidad (ancla = hoy GDL). `cmp` = rótulo del periodo comparado:
 *  la ventana previa de IGUAL duración → fórmula de crecimiento `(reciente/previa)−1`. */
const RANGE_PRESETS: ReadonlyArray<{ key: string; label: string; range: (today: string) => { from: string; to: string }; cmp: string }> = [
  { key: '1D', label: 'Hoy', range: (t) => ({ from: t, to: t }), cmp: 'vs ayer' },
  { key: '1M', label: '30 días', range: (t) => ({ from: ymdDaysAgo(t, 29), to: t }), cmp: 'vs 30 días previos' },
  { key: '3M', label: '3M', range: (t) => ({ from: ymdDaysAgo(t, 89), to: t }), cmp: 'vs 3 meses previos' },
  { key: '6M', label: '6M', range: (t) => ({ from: ymdDaysAgo(t, 179), to: t }), cmp: 'vs 6 meses previos' },
  { key: '1Y', label: '1A', range: (t) => ({ from: ymdDaysAgo(t, 364), to: t }), cmp: 'vs año previo' },
  { key: 'YTD', label: 'Año', range: (t) => ({ from: `${t.slice(0, 4)}-01-01`, to: t }), cmp: 'vs mismo periodo previo' },
]

/** Δ% por ventana móvil (texto + clase verde/rojo). `cmpLabel` describe la ventana previa. */
function deltaPct(cur: number, prev: number | null | undefined, cmpLabel = 'vs periodo anterior'): { txt: string; cls: string } {
  if (prev === null || prev === undefined || prev === 0) return { txt: `— ${cmpLabel}`, cls: '' }
  const d = ((cur - prev) / prev) * 100
  return { txt: `${d >= 0 ? '+' : ''}${d.toFixed(1)}% ${cmpLabel}`, cls: d >= 0 ? 'dhk8-rep__pos' : 'dhk8-rep__neg' }
}

interface Agg {
  count: number
  salesCents: number
  tipCents: number
  byProduct: { id: string; name: string; qty: number; cents: number }[]
  byServer: { id: string; count: number; cents: number }[]
}

function aggregate(orders: BackendOrderDto[]): Agg {
  let salesCents = 0, tipCents = 0
  const prod = new Map<string, { name: string; qty: number; cents: number }>()
  const srv = new Map<string, { count: number; cents: number }>()
  for (const o of orders) {
    salesCents += o.totalCents
    tipCents += o.tipCents
    const sk = o.openedByUserId || o.closedByUserId || 'unknown'
    const s = srv.get(sk) ?? { count: 0, cents: 0 }
    s.count += 1; s.cents += o.totalCents; srv.set(sk, s)
    for (const it of o.items ?? []) {
      const k = it.productId || it.name
      const p = prod.get(k) ?? { name: it.name, qty: 0, cents: 0 }
      p.qty += it.quantity; p.cents += it.totalCents; prod.set(k, p)
    }
  }
  return {
    count: orders.length,
    salesCents,
    tipCents,
    byProduct: [...prod.entries()].map(([id, v]) => ({ id, ...v })).sort((a, b) => b.cents - a.cents),
    byServer: [...srv.entries()].map(([id, v]) => ({ id, ...v })).sort((a, b) => b.cents - a.cents),
  }
}

export default function ReportsPage() {
  const branchId = useAuthStore((s) => s.user?.branchId)
  const tenantId = useTenantId()
  const [fromDay, setFromDay] = useState(() => gdlToday())
  const [toDay, setToDay] = useState(() => gdlToday())
  const [preset, setPreset] = useState<string>('1D')
  const [orders, setOrders] = useState<BackendOrderDto[]>([])
  const [tips, setTips] = useState<TipSummaryDto | null>(null)
  const [lastZ, setLastZ] = useState<CashCutDto | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Ola 2 — datos del grafo (erp-reports): IVA / métodos de pago / cancelaciones+descuentos + comparativo.
  const [taxes, setTaxes] = useState<{ ordersCount: number; subtotalCents: number; taxCents: number; totalCents: number } | null>(null)
  const [payMethods, setPayMethods] = useState<Array<{ method: string; count: number; amountCents: number }>>([])
  const [voids, setVoids] = useState<{ cancelledCount: number; cancelledCents: number; discountedOrders: number; discountCents: number } | null>(null)
  const [prevTotals, setPrevTotals] = useState<{ ordersCount: number; netCents: number } | null>(null)

  useEffect(() => {
    const ctrl = new AbortController()
    if (!tenantId) { setError('Sesión no inicializada'); return }
    // Reset on tenant change so the previous tenant's rows don't briefly
    // show under the new context.
    setOrders([]); setTips(null); setLastZ(null)
    const from = fromDateInput(fromDay, false)
    const to = fromDateInput(toDay, true)
    setLoading(true); setError(null)
    Promise.all([
      listOrders({ tenantId, ...(branchId ? { branchId } : {}), status: 'CLOSED', from, to, limit: 100 }, ctrl.signal).then((r) => r.items).catch(() => [] as BackendOrderDto[]),
      tipSummary({ tenantId, ...(branchId ? { branchId } : {}), from, to }, ctrl.signal).catch(() => null),
      latestCashCut({ ...(branchId ? { branchId } : {}), kind: 'Z_REPORT' }, ctrl.signal).catch(() => null),
    ]).then(([o, t, z]) => {
      setOrders(o); setTips(t); setLastZ(z); setLoading(false)
    }).catch((e) => { setError(e instanceof Error ? e.message : 'No se pudo cargar el reporte'); setLoading(false) })
    return () => ctrl.abort()
  }, [fromDay, toDay, branchId, tenantId])

  // Ola 2 — reportes del grafo (erp-reports): se pasan los DÍAS (yyyy-mm-dd);
  // el backend los bucketea por día de negocio GDL. Cada query falla a vacío
  // (.catch) sin romper la página. Incluye totales del periodo ANTERIOR (Δ%).
  useEffect(() => {
    if (!tenantId) return
    const ctrl = new AbortController()
    const ctx = { fetchOptions: { signal: ctrl.signal } }
    const vars = { from: fromDay, to: toDay, ...(branchId ? { branchId } : {}) }
    setTaxes(null); setPayMethods([]); setVoids(null); setPrevTotals(null)
    const pv = prevRange(fromDay, toDay)
    Promise.all([
      gqlClient.query(PosReportTaxesQuery, vars, ctx).toPromise().then((r) => r.data?.reportTaxes ?? null).catch(() => null),
      gqlClient.query(PosReportPaymentMethodsQuery, vars, ctx).toPromise().then((r) => r.data?.reportPaymentMethods ?? []).catch(() => []),
      gqlClient.query(PosReportVoidsDiscountsQuery, vars, ctx).toPromise().then((r) => r.data?.reportVoidsDiscounts ?? null).catch(() => null),
      gqlClient.query(PosReportSalesTotalsQuery, { from: pv.from, to: pv.to, ...(branchId ? { branchId } : {}) }, ctx).toPromise().then((r) => r.data?.reportSales?.totals ?? null).catch(() => null),
    ]).then(([t, pm, v, prev]) => {
      setTaxes(t)
      setPayMethods(pm ? [...pm] : [])
      setVoids(v)
      setPrevTotals(prev)
    }).catch(() => { /* cada query ya cae a vacío; no rompemos la página */ })
    return () => ctrl.abort()
  }, [fromDay, toDay, branchId, tenantId])

  const agg = useMemo(() => aggregate(orders), [orders])
  const avgCents = agg.count > 0 ? Math.round(agg.salesCents / agg.count) : 0

  // Ola 2 — donut de métodos de pago (datos REALES) + Δ% comparativo.
  const paymentChart = useMemo(
    () => payMethods.map((p) => ({ name: METHOD_LABELS[p.method] ?? p.method, value: p.amountCents / 100 })),
    [payMethods],
  )
  const cmpLabel = RANGE_PRESETS.find((p) => p.key === preset)?.cmp ?? 'vs periodo anterior'
  const applyPreset = (p: (typeof RANGE_PRESETS)[number]) => { const r = p.range(gdlToday()); setFromDay(r.from); setToDay(r.to); setPreset(p.key) }
  const ventasDelta = deltaPct(agg.salesCents, prevTotals?.netCents, cmpLabel)
  const ordenesDelta = deltaPct(agg.count, prevTotals?.ordersCount, cmpLabel)

  // Lane #171 — derivaciones para charts.
  // Ventas por día (area): agrupa por YYYY-MM-DD y suma totalCents.
  const salesByDay = useMemo(() => {
    const m = new Map<string, { day: string; sales: number; orders: number }>()
    for (const o of orders) {
      const key = gdlYmd(o.closedAt ?? o.openedAt)
      const cur = m.get(key) ?? { day: key, sales: 0, orders: 0 }
      cur.sales += o.totalCents / 100
      cur.orders += 1
      m.set(key, cur)
    }
    return Array.from(m.values()).sort((a, b) => a.day.localeCompare(b.day))
  }, [orders])

  // Ventas por hora (area) — útil para detectar picos.
  const salesByHour = useMemo(() => {
    const buckets: Array<{ hour: string; sales: number }> = Array.from({ length: 24 }, (_, h) => ({
      hour: String(h).padStart(2, '0') + 'h',
      sales: 0,
    }))
    for (const o of orders) {
      const h = gdlHour(o.closedAt ?? o.openedAt)
      if (buckets[h]) buckets[h].sales += o.totalCents / 100
    }
    // Recortamos las horas con 0 al inicio/fin para que el chart no se vea raro.
    let lo = 0, hi = buckets.length - 1
    while (lo < hi && buckets[lo]!.sales === 0) lo++
    while (hi > lo && buckets[hi]!.sales === 0) hi--
    return buckets.slice(lo, hi + 1)
  }, [orders])

  // Top productos (bar horizontal) — top 8 por ingreso.
  const topProductsChart = useMemo(
    () => agg.byProduct.slice(0, 8).map((p) => ({ name: p.name, sales: p.cents / 100 })),
    [agg.byProduct],
  )

  // Formatter para tooltip + axis Y de los charts de ventas.
  const formatMxn = (n: number) =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n)

  return (
    <div className="dhk8-rep">
      <header className="dhk8-rep__head">
        <div>
          <h1 className="dhk8-rep__title">Reportes</h1>
          <p className="dhk8-rep__sub">Ventas cerradas, productos top, mesero top y propinas del periodo.</p>
        </div>
        <div className="dhk8-rep__range">
          <label>Desde<input type="date" value={fromDay} onChange={(e) => { setFromDay(e.target.value); setPreset('custom') }} /></label>
          <label>Hasta<input type="date" value={toDay} onChange={(e) => { setToDay(e.target.value); setPreset('custom') }} /></label>
        </div>
      </header>

      <div className="dhk8-rep__presets" role="group" aria-label="Periodo">
        {RANGE_PRESETS.map((p) => (
          <button key={p.key} type="button" aria-pressed={preset === p.key}
            className={`dhk8-rep__preset${preset === p.key ? ' dhk8-rep__preset--on' : ''}`}
            onClick={() => applyPreset(p)}>{p.label}</button>
        ))}
        {preset === 'custom' && <span className="dhk8-rep__preset-custom">Personalizado</span>}
      </div>

      {error && <div className="dhk8-rep__error">{error}</div>}

      <div className="dhk8-rep__kpis">
        <Kpi label="Ventas (cerradas)" value={formatCents(agg.salesCents)} sub={<span className={ventasDelta.cls}>{ventasDelta.txt}</span>} />
        <Kpi label="Órdenes" value={String(agg.count)} sub={<span className={ordenesDelta.cls}>{ordenesDelta.txt}</span>} />
        <Kpi label="Ticket promedio" value={formatCents(avgCents)} />
        <Kpi label="Propinas" value={formatCents(agg.tipCents)} />
        <Kpi label="IVA cobrado" value={formatCents(taxes?.taxCents ?? 0)} />
      </div>

      {/* Lane #171 — paneles visuales con charts NintaiGO. */}
      {orders.length > 0 && (
        <div className="dhk8-rep__charts">
          <section className="dhk8-rep__card">
            <h2>Ventas por día</h2>
            <AreaChartNG
              data={salesByDay}
              xKey="day"
              series={[{ key: 'sales', label: 'Ventas' }]}
              height={220}
              valueFormatter={formatMxn}
              hideLegend
            />
          </section>

          {salesByHour.length > 1 && (
            <section className="dhk8-rep__card">
              <h2>Ventas por hora</h2>
              <AreaChartNG
                data={salesByHour}
                xKey="hour"
                series={[{ key: 'sales', label: 'Ventas' }]}
                height={220}
                valueFormatter={formatMxn}
                hideLegend
              />
            </section>
          )}

          {topProductsChart.length > 0 && (
            <section className="dhk8-rep__card">
              <h2>Productos top (ingreso)</h2>
              <BarChartNG
                data={topProductsChart}
                xKey="name"
                series={[{ key: 'sales', label: 'Ingreso' }]}
                height={Math.max(220, topProductsChart.length * 32)}
                valueFormatter={formatMxn}
                horizontal
                hideLegend
              />
            </section>
          )}

        </div>
      )}

      <div className="dhk8-rep__cols">
        <section className="dhk8-rep__card">
          <h2>Métodos de pago</h2>
          {paymentChart.length === 0 ? <p className="dhk8-rep__empty">Sin pagos en el periodo.</p> : (
            <PieChartNG data={paymentChart} height={220} donut valueFormatter={formatMxn} />
          )}
        </section>

        <section className="dhk8-rep__card">
          <h2>Cancelaciones y descuentos</h2>
          {!voids ? <p className="dhk8-rep__empty">Sin datos en el periodo.</p> : (
            <table className="dhk8-rep__table dhk8-rep__table--kv">
              <tbody>
                <tr><td>Cancelaciones</td><td>{voids.cancelledCount} · {formatCents(voids.cancelledCents)}</td></tr>
                <tr><td>Órdenes con descuento</td><td>{voids.discountedOrders} · {formatCents(voids.discountCents)}</td></tr>
              </tbody>
            </table>
          )}
        </section>
      </div>

      <div className="dhk8-rep__cols">
        <section className="dhk8-rep__card">
          <h2>Productos top</h2>
          {loading && orders.length === 0 ? <p className="dhk8-rep__empty">Cargando…</p> : agg.byProduct.length === 0 ? <p className="dhk8-rep__empty">Sin ventas en el periodo.</p> : (
            <table className="dhk8-rep__table">
              <thead><tr><th>#</th><th>Producto</th><th>Cant.</th><th>Importe</th></tr></thead>
              <tbody>{agg.byProduct.slice(0, 15).map((p, i) => (
                <tr key={p.id}><td>{i + 1}</td><td>{p.name}</td><td>{p.qty}</td><td>{formatCents(p.cents)}</td></tr>
              ))}</tbody>
            </table>
          )}
        </section>

        <section className="dhk8-rep__card">
          <h2>Mesero top</h2>
          {loading && orders.length === 0 ? <p className="dhk8-rep__empty">Cargando…</p> : agg.byServer.length === 0 ? <p className="dhk8-rep__empty">Sin ventas en el periodo.</p> : (
            <table className="dhk8-rep__table">
              <thead><tr><th>#</th><th>Mesero</th><th>Órdenes</th><th>Ventas</th></tr></thead>
              <tbody>{agg.byServer.slice(0, 15).map((s, i) => (
                <tr key={s.id}><td>{i + 1}</td><td title={s.id}>{shortId(s.id)}</td><td>{s.count}</td><td>{formatCents(s.cents)}</td></tr>
              ))}</tbody>
            </table>
          )}
        </section>
      </div>

      <div className="dhk8-rep__cols">
        <section className="dhk8-rep__card">
          <h2>Propinas — reparto</h2>
          {!tips ? <p className="dhk8-rep__empty">Sin propinas en el periodo.</p> : (
            <>
              <div className="dhk8-rep__poolbar">
                <span>Pool a repartir: <strong>{formatCents(tips.pooledTotalCents)}</strong></span>
                <span>Directas: <strong>{formatCents(tips.directTotalCents)}</strong></span>
                <span>Total: <strong>{formatCents(tips.totalCents)}</strong></span>
              </div>
              {tips.byServer.length === 0 ? <p className="dhk8-rep__empty">—</p> : (
                <table className="dhk8-rep__table">
                  <thead><tr><th>Mesero</th><th>Pool</th><th>Directas</th><th>Total</th></tr></thead>
                  <tbody>{[...tips.byServer].sort((a, b) => b.totalCents - a.totalCents).map((r) => (
                    <tr key={r.serverId}><td title={r.serverId}>{shortId(r.serverId)}</td><td>{formatCents(r.pooledCents)}</td><td>{formatCents(r.directCents)}</td><td>{formatCents(r.totalCents)}</td></tr>
                  ))}</tbody>
                </table>
              )}
            </>
          )}
        </section>

        <section className="dhk8-rep__card">
          <h2>Último corte Z</h2>
          {!lastZ ? <p className="dhk8-rep__empty">Aún no hay un corte Z.</p> : (
            <table className="dhk8-rep__table dhk8-rep__table--kv">
              <tbody>
                <tr><td>Fecha</td><td>{new Date(lastZ.createdAt).toLocaleString('es-MX', { timeZone: 'America/Mexico_City' })}</td></tr>
                <tr><td>Esperado</td><td>{formatCents(lastZ.expectedCents)}</td></tr>
                <tr><td>Contado</td><td>{formatCents(lastZ.countedCents)}</td></tr>
                <tr><td>Diferencia</td><td className={lastZ.varianceCents === 0 ? '' : lastZ.varianceCents > 0 ? 'dhk8-rep__pos' : 'dhk8-rep__neg'}>
                  {lastZ.varianceCents === 0 ? '✓ cuadró' : `${lastZ.varianceCents > 0 ? '+' : '−'}${formatCents(Math.abs(lastZ.varianceCents))}`}
                </td></tr>
                {lastZ.notes && <tr><td>Notas</td><td>{lastZ.notes}</td></tr>}
              </tbody>
            </table>
          )}
        </section>
      </div>

      <style>{styles}</style>
    </div>
  )
}

function Kpi({ label, value, sub }: { label: string; value: string; sub?: ReactNode }) {
  return <div className="dhk8-rep__kpi"><div className="dhk8-rep__kpi-l">{label}</div><div className="dhk8-rep__kpi-v">{value}</div>{sub ? <div className="dhk8-rep__kpi-d">{sub}</div> : null}</div>
}

// HIG-green: rounded cards, hairline separators, soft elevation, no harsh borders.
const styles = `
  .dhk8-rep { display:flex; flex-direction:column; gap:var(--pos-space-4,16px); }
  .dhk8-rep__head { display:flex; justify-content:space-between; align-items:flex-start; gap:var(--pos-space-3,12px); flex-wrap:wrap; }
  .dhk8-rep__title { font-family:var(--font-display,inherit); font-size:var(--pos-text-xl,20px); font-weight:var(--pos-weight-semibold,600); letter-spacing:var(--pos-tracking-snug,-0.005em); margin:0; color:var(--pos-fg,var(--text-primary,#E8EFE9)); }
  .dhk8-rep__sub { margin:0; font-family:var(--font-display,inherit); font-size:var(--pos-text-xs,12px); color:var(--pos-fg-tertiary,var(--text-tertiary,#7E8C82)); }
  .dhk8-rep__range { display:flex; gap:var(--pos-space-3,12px); flex-wrap:wrap; }
  .dhk8-rep__range label { flex:1; min-width:130px; }
  .dhk8-rep__range label { display:flex; flex-direction:column; gap:var(--pos-space-1,4px); font-family:var(--font-display,inherit); font-size:var(--pos-text-xxs,11px); font-weight:var(--pos-weight-medium,500); letter-spacing:var(--pos-tracking-wide,.04em); text-transform:uppercase; color:var(--pos-fg-tertiary,var(--text-tertiary,#7E8C82)); }
  .dhk8-rep__range input { min-height:44px; border:1px solid var(--pos-separator-strong,var(--separator,rgba(255,255,255,.14))); padding:var(--pos-space-2,8px) var(--pos-space-3,12px); background:var(--pos-canvas,var(--bg-canvas,#0F1411)); color:var(--pos-fg,var(--text-primary,#E8EFE9)); font-family:var(--font-display,inherit); font-size:var(--pos-text-sm,13px); border-radius:var(--pos-radius-md,8px); transition:border-color var(--pos-duration-fast,120ms) var(--pos-ease-out,ease), box-shadow var(--pos-duration-fast,120ms) var(--pos-ease-out,ease); }
  .dhk8-rep__range input:focus-visible { outline:none; border-color:var(--pos-tint,var(--accent,#1F8B3A)); box-shadow:0 0 0 3px var(--pos-tint-soft,color-mix(in oklch,#1F8B3A 12%,transparent)); }
  .dhk8-rep__presets { display:flex; flex-wrap:wrap; gap:var(--pos-space-2,8px); }
  .dhk8-rep__preset { min-height:36px; padding:0 var(--pos-space-3,12px); border:1px solid var(--pos-separator-strong,var(--separator,rgba(255,255,255,.14))); background:var(--pos-surface,var(--bg-surface,#161C18)); color:var(--pos-fg-secondary,var(--text-secondary,#B6BFB8)); font-family:var(--font-display,inherit); font-size:var(--pos-text-sm,13px); font-weight:var(--pos-weight-medium,500); border-radius:var(--pos-radius-md,8px); cursor:pointer; transition:border-color var(--pos-duration-fast,120ms) var(--pos-ease-out,ease), background var(--pos-duration-fast,120ms) var(--pos-ease-out,ease); }
  .dhk8-rep__preset:hover { border-color:var(--pos-tint,var(--accent,#1F8B3A)); }
  .dhk8-rep__preset--on { background:var(--pos-tint,var(--accent,#1F8B3A)); border-color:var(--pos-tint,var(--accent,#1F8B3A)); color:#fff; }
  .dhk8-rep__preset-custom { align-self:center; font-family:var(--font-display,inherit); font-size:var(--pos-text-xs,12px); color:var(--pos-fg-tertiary,var(--text-tertiary,#7E8C82)); }
  .dhk8-rep__error { padding:var(--pos-space-2,8px) var(--pos-space-3,12px); background:color-mix(in oklch,var(--pos-danger,#E5484D) 14%,transparent); color:var(--pos-danger,var(--status-error,#E5484D)); font-family:var(--font-display,inherit); font-size:var(--pos-text-xs,12px); border-radius:var(--pos-radius-md,8px); }
  .dhk8-rep__kpis { display:grid; grid-template-columns:repeat(auto-fit,minmax(160px,1fr)); gap:var(--pos-space-3,12px); }
  .dhk8-rep__kpi { padding:var(--pos-space-3,12px) var(--pos-space-4,16px); border:1px solid var(--pos-separator,var(--separator-opaque,rgba(255,255,255,.07))); background:var(--pos-surface,var(--bg-surface,#161C18)); border-radius:var(--pos-radius-lg,12px); box-shadow:var(--pos-shadow-1,0 1px 2px rgba(0,0,0,.18)); }
  .dhk8-rep__kpi-l { font-family:var(--font-display,inherit); font-size:var(--pos-text-xxs,11px); font-weight:var(--pos-weight-medium,500); letter-spacing:var(--pos-tracking-wide,.04em); text-transform:uppercase; color:var(--pos-fg-tertiary,var(--text-tertiary,#7E8C82)); }
  .dhk8-rep__kpi-v { font-family:var(--font-display,inherit); font-size:var(--pos-text-lg,17px); font-weight:var(--pos-weight-semibold,600); font-variant-numeric:tabular-nums; color:var(--pos-fg,var(--text-primary,#E8EFE9)); margin-top:var(--pos-space-1,4px); }
  .dhk8-rep__kpi-d { margin-top:var(--pos-space-1,4px); font-family:var(--font-display,inherit); font-size:var(--pos-text-xxs,11px); font-weight:var(--pos-weight-medium,500); color:var(--pos-fg-tertiary,var(--text-tertiary,#7E8C82)); }
  .dhk8-rep__cols { display:grid; grid-template-columns:repeat(auto-fit,minmax(320px,1fr)); gap:var(--pos-space-4,16px); }
  /* Lane #171 — grilla de charts. Cada card colapsa <720px. */
  .dhk8-rep__charts { display:grid; grid-template-columns:repeat(auto-fit,minmax(360px,1fr)); gap:var(--pos-space-4,16px); }
  .dhk8-rep__hint { font-size:var(--pos-text-xs,11px); color:var(--pos-fg-tertiary,#7E8C82); font-weight:var(--pos-weight-medium,500); margin-left:var(--pos-space-1,4px); }
  .dhk8-rep__card { padding:var(--pos-space-4,16px); border:1px solid var(--pos-separator,var(--separator-opaque,rgba(255,255,255,.07))); background:var(--pos-surface,var(--bg-surface,#161C18)); border-radius:var(--pos-radius-lg,12px); box-shadow:var(--pos-shadow-1,0 1px 2px rgba(0,0,0,.18)); display:flex; flex-direction:column; gap:var(--pos-space-3,12px); min-width:0; overflow-x:auto; }
  .dhk8-rep__table { table-layout:auto; }
  .dhk8-rep__table td:nth-child(2) { word-break:break-word; }
  .dhk8-rep__card h2 { font-family:var(--font-display,inherit); font-size:var(--pos-text-md,15px); font-weight:var(--pos-weight-semibold,600); margin:0; color:var(--pos-fg,var(--text-primary,#E8EFE9)); }
  .dhk8-rep__empty { font-family:var(--font-display,inherit); font-size:var(--pos-text-xs,12px); color:var(--pos-fg-tertiary,var(--text-tertiary,#7E8C82)); margin:0; }
  .dhk8-rep__table { width:100%; border-collapse:collapse; font-family:var(--font-display,inherit); font-size:var(--pos-text-xs,12px); font-variant-numeric:tabular-nums; }
  .dhk8-rep__table th,.dhk8-rep__table td { padding:var(--pos-space-2,8px) var(--pos-space-2,8px); text-align:left; border-bottom:1px solid var(--pos-separator,var(--separator,rgba(255,255,255,.07))); color:var(--pos-fg-secondary,var(--text-secondary,#B6BFB8)); }
  .dhk8-rep__table tbody tr:last-child td { border-bottom:none; }
  .dhk8-rep__table th { color:var(--pos-fg-tertiary,var(--text-tertiary,#7E8C82)); font-size:var(--pos-text-xxs,11px); font-weight:var(--pos-weight-medium,500); letter-spacing:var(--pos-tracking-wide,.04em); text-transform:uppercase; }
  .dhk8-rep__table--kv td:first-child { color:var(--pos-fg-tertiary,var(--text-tertiary,#7E8C82)); width:35%; }
  .dhk8-rep__poolbar { display:flex; gap:var(--pos-space-4,16px); flex-wrap:wrap; font-family:var(--font-display,inherit); font-size:var(--pos-text-xs,12px); color:var(--pos-fg-secondary,var(--text-secondary,#B6BFB8)); }
  .dhk8-rep__pos { color:var(--pos-success,var(--status-success,#8BC34A)); } .dhk8-rep__neg { color:var(--pos-danger,var(--status-error,#E5484D)); }
`
